import affiliateCommission from "@assets/img/affiliate-commission.png";
import extraIncome from "@assets/img/extra-income.png";
import raffleProducts from "@assets/img/raffle-products.png";
import type { ReactNode } from "react";

import "./RafflePromoSection.css";

interface RaffleAdvertisingProps {
  onCreateRaffle?: () => void;
}

interface SmallCardProps {
  icon: string;
  title: string;
  text: string;
  color: "orange" | "green" | "blue" | "red";
}

interface FeatureCardProps {
  icon: string;
  title: string;
  text: string;
  color: "purple" | "green" | "orange";
}

interface DarkFeatureProps {
  icon: string;
  title: string;
  text: string;
}

export function RaffleAdvertising({ onCreateRaffle }: RaffleAdvertisingProps) {
  return (
    <section className="raffle-advertising container px-3 px-md-4 px-lg-0 my-4">
      <article className="raffle-ad-panel raffle-ad-panel-products">
        <div className="raffle-ad-copy">
          <Badge icon="bi-ticket-perforated-fill" color="orange">
            Rife o que quiser
          </Badge>

          <h2>
            Qualquer pessoa pode{" "}
            <span className="raffle-text-orange">criar uma rifa.</span>
          </h2>

          <p>
            Rife produtos novos, usados, valores em dinheiro, cupons e muito
            mais. De forma simples, rápida e segura.
          </p>

          <CreateButton color="orange" onClick={onCreateRaffle} />
        </div>

        <div className="raffle-ad-image raffle-ad-products-image">
          <img
            src={raffleProducts}
            alt="Caixa com produtos para criar uma rifa"
          />
        </div>

        <div className="raffle-ad-small-grid">
          <SmallCard
            icon="bi-box-seam"
            title="Produtos novos"
            text="Eletrônicos, móveis e muito mais."
            color="orange"
          />
          <SmallCard
            icon="bi-arrow-repeat"
            title="Produtos usados"
            text="Rife o que já não usa mais."
            color="green"
          />
          <SmallCard
            icon="bi-cash-coin"
            title="Valores em dinheiro"
            text="Qualquer quantia, de forma segura."
            color="blue"
          />
          <SmallCard
            icon="bi-ticket-perforated"
            title="Cupons e experiências"
            text="Viagens, eventos e muito mais."
            color="red"
          />
        </div>
      </article>

      <article className="raffle-ad-panel raffle-ad-panel-affiliates">
        <div className="raffle-ad-copy">
          <Badge icon="bi-people-fill" color="blue">
            Venda com afiliados
          </Badge>

          <h2>
            Convide amigos, influencers e{" "}
            <span className="raffle-text-blue">ganhe mais.</span>
          </h2>

          <p>
            Eles divulgam sua rifa, vendem os tickets e recebem as comissões
            diretamente na conta deles, de forma automática e sem complicação.
          </p>

          <CreateButton color="blue" onClick={onCreateRaffle} />
        </div>

        <div className="raffle-ad-image raffle-ad-affiliate-image">
          <img
            src={affiliateCommission}
            alt="Pessoa recebendo comissão de afiliado"
          />
        </div>

        <div className="raffle-ad-feature-stack">
          <FeatureCard
            icon="bi-people-fill"
            title="Você define a comissão"
            text="Escolha o percentual para cada afiliado."
            color="purple"
          />
          <FeatureCard
            icon="bi-shield-fill-check"
            title="Vendas e pagamento automático"
            text="Os tickets são reconhecidos e a comissão vai direto para a conta do afiliado."
            color="green"
          />
          <FeatureCard
            icon="bi-megaphone-fill"
            title="Divulgue para quem quiser"
            text="Amigos, familiares e influencers."
            color="orange"
          />
        </div>
      </article>

      <article className="raffle-ad-panel raffle-ad-panel-income">
        <div className="raffle-ad-copy raffle-ad-copy-dark">
          <Badge icon="bi-lightning-charge-fill" color="dark">
            Desde um item até uma renda extra
          </Badge>

          <h2>
            Rife aquele item que já não usa ou crie uma campanha para levantar{" "}
            <span className="raffle-text-gold">dinheiro extra.</span>
          </h2>

          <p>
            Qualquer motivo é válido. É simples, seguro e todo o processo é
            transparente, desde a venda até o sorteio.
          </p>

          <CreateButton color="orange" onClick={onCreateRaffle} />
        </div>

        <div className="raffle-ad-image raffle-ad-income-image">
          <img src={extraIncome} alt="Pessoa criando renda extra com rifa" />
        </div>

        <div className="raffle-ad-dark-features">
          <DarkFeature
            icon="bi-star"
            title="Qualquer pessoa"
            text="Sem burocracia e 100% online."
          />
          <DarkFeature
            icon="bi-shield-check"
            title="Totalmente seguro"
            text="Pagamentos e controle automáticos."
          />
          <DarkFeature
            icon="bi-trophy"
            title="Mais oportunidades"
            text="Para objetivos, sonhos ou emergências."
          />
        </div>
      </article>
    </section>
  );
}

function Badge({
  icon,
  color,
  children,
}: {
  icon: string;
  color: "orange" | "blue" | "dark";
  children: ReactNode;
}) {
  return (
    <div className={`raffle-ad-badge raffle-ad-badge-${color}`}>
      <i className={`bi ${icon}`} />
      <span>{children}</span>
    </div>
  );
}

function CreateButton({
  color,
  onClick,
}: {
  color: "orange" | "blue";
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      className={`raffle-ad-button raffle-ad-button-${color}`}
      onClick={onClick}
    >
      <i className="bi bi-ticket-perforated-fill" />
      <span>Quero criar a minha rifa</span>
      <i className="bi bi-arrow-right" />
    </button>
  );
}

function SmallCard({ icon, title, text, color }: SmallCardProps) {
  return (
    <div className="raffle-ad-small-card">
      <i className={`bi ${icon} raffle-small-icon raffle-small-${color}`} />
      <strong>{title}</strong>
      <small>{text}</small>
    </div>
  );
}

function FeatureCard({ icon, title, text, color }: FeatureCardProps) {
  return (
    <div className="raffle-ad-feature-card">
      <div className={`raffle-feature-icon raffle-feature-${color}`}>
        <i className={`bi ${icon}`} />
      </div>
      <div>
        <strong>{title}</strong>
        <small>{text}</small>
      </div>
    </div>
  );
}

function DarkFeature({ icon, title, text }: DarkFeatureProps) {
  return (
    <div className="raffle-ad-dark-feature">
      <i className={`bi ${icon}`} />
      <strong>{title}</strong>
      <small>{text}</small>
    </div>
  );
}
